import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link2, Inbox, CheckCircle2, MousePointerClick } from "lucide-react";

export default function OverviewPage({ user }) {
  const [stats, setStats] = useState({ links: 0, publicLinks: 0, emails: 0, clicks: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [links, emails] = await Promise.all([api.get("/links"), api.get("/emails")]);
      const pub = links.data.filter((l) => l.is_public);
      const clicks = links.data.reduce((s, l) => s + (l.clicks || 0), 0);
      setStats({ links: links.data.length, publicLinks: pub.length, emails: emails.data.length, clicks });
      setRecent(links.data.slice(0, 5));
    })();
  }, []);

  const cards = [
    { label: "Total Links", value: stats.links, icon: Link2, testid: "stat-links" },
    { label: "Live on Page", value: stats.publicLinks, icon: CheckCircle2, testid: "stat-public" },
    { label: "Emails Parsed", value: stats.emails, icon: Inbox, testid: "stat-emails" },
    { label: "Clicks", value: stats.clicks, icon: MousePointerClick, testid: "stat-clicks" },
  ];

  return (
    <div className="p-10 max-w-6xl">
      <div className="flex items-baseline justify-between mb-10">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Overview</div>
          <h1 className="font-heading font-light text-4xl tracking-tighter mt-2">
            Good to see you, {user.name?.split(" ")[0] || "there"}.
          </h1>
        </div>
        {user.handle && (
          <a
            href={`/u/${user.handle}`}
            target="_blank"
            rel="noreferrer"
            data-testid="public-handle-link"
            className="text-sm border border-zinc-300 hover:bg-zinc-950 hover:text-white hover:border-zinc-950 px-4 py-2 rounded-sm transition-colors"
          >
            /u/{user.handle}
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="border border-zinc-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <c.icon className="w-4 h-4 text-zinc-400" />
              <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">{c.label}</div>
            </div>
            <div className="font-heading font-light text-4xl tracking-tighter mt-4">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="border border-zinc-200 bg-white">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-sm font-medium">Recent captures</div>
          <a href="/dashboard/links" data-testid="see-all-links" className="text-xs text-zinc-500 hover:text-zinc-950">See all →</a>
        </div>
        <div className="divide-y divide-zinc-100">
          {recent.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">
              Nothing yet. Forward an email or paste one under <a className="underline" href="/dashboard/emails">Emails</a>.
            </div>
          ) : (
            recent.map((l) => (
              <div key={l.id} className="px-6 py-4 flex items-center gap-4">
                <div className={`w-1.5 h-8 rounded-full ${l.is_public ? "bg-emerald-500" : "bg-amber-400"}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{l.title || l.url}</div>
                  <div className="text-xs text-zinc-500 truncate font-mono">{l.url}</div>
                </div>
                <div className="text-xs text-zinc-500">{l.category_name || "—"}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
